import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  getAttrConfig,
  getAttributionZonesForPOI,
  getNewAttributionZonesData,
  VenuesAttributionSettingsModal
} from 'sonata-platform-components';
import { ButtonUI, ContextualMenu, showNotification } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { useGroupingModals } from '../../../grouping/components';
import { getGroupActionsAvailability } from '../../../grouping/domain/groupRules';
import { useGrouping } from '../../../grouping/hooks/useGrouping';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import {
  buildEnabledFootfallAttributionSettings,
  needsAttributionSettingsUpdate,
  resolveAttributionMode
} from '../../../MultiFlightMap.helpers';
import { AttributionRadiusPrompt } from '../../AttributionPOI/AttributionRadiusPrompt/AttributionRadiusPrompt';
import ConcentricLocationsModal from '../../ConcentricLocationsModal/ConcentricLocationsModal';

import './listActionButtons.scss';

import { disableAttributionLocations } from '@/helpers/common/attribution.helper';

import type { AttributionZones, ListType } from '../../../multiFlightMap.types';

type ListActionButton = {
  id: string;
  icon: string;
  text: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  /** Defaults to true: the entry is only offered when rows are selected. */
  requiresSelection?: boolean;
};

type Props = {
  id: string;
};

type VenuesAttributionSettingsModalProps = React.ComponentProps<typeof VenuesAttributionSettingsModal>;

type State = {
  addLocationStatus: 'idle' | 'loading' | 'success' | 'error';
  showConcentricLocationsModal: boolean;
  showEditRadiusModal: boolean;
  showLocationRadiusModal: boolean;
  showVenuesAttributionSettingsModal: boolean;
};

function ListActionButtons({ id }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const {
    activeSubsection,
    attributionSettings,
    attributionZones: allAttributionZones,
    createAttributionZonesForVenues,
    disableMultipleItems,
    enableMultipleItems,
    entityId,
    entityType,
    fullShapes,
    list,
    onAddLocationsForVenueIds,
    selectedItems,
    setAttributionSettings,
    setAttributionZones,
    setLocationSmart,
    setSelectedItems,
    temporaryVenues,
    temporaryZones: locations,
    type,
    user,
    userRoles,
    updateZone
  } = useMultiFlightMap();
  const { deleteItems, openGroupModal } = useGroupingModals();
  const { addableGroups, editableGroups } = useGrouping(list);

  const [
    {
      addLocationStatus,
      showConcentricLocationsModal,
      showEditRadiusModal,
      showLocationRadiusModal,
      showVenuesAttributionSettingsModal
    },
    setState
  ] = useState<State>({
    addLocationStatus: 'idle',
    showConcentricLocationsModal: false,
    showEditRadiusModal: false,
    showLocationRadiusModal: false,
    showVenuesAttributionSettingsModal: false
  });

  const handleEnableLocationsClick = React.useCallback(() => {
    const selectedLocationIds = selectedItems.locations;
    const locationsToEnable = locations.filter(l => !l.deleted && selectedLocationIds?.includes(l._id!));
    if (locationsToEnable.length > 0) {
      enableMultipleItems(list, locationsToEnable as any);
    }
    setSelectedItems(list, []);
  }, [selectedItems.locations, locations, enableMultipleItems, setSelectedItems]);

  const handleDisableLocationsClick = React.useCallback(() => {
    const selectedLocationIds = selectedItems.locations;
    const locationsToDisable = locations.filter(l => !l.deleted && selectedLocationIds?.includes(l._id!));
    if (locationsToDisable.length > 0) {
      disableMultipleItems(list, locationsToDisable as any);
    }
    setSelectedItems(list, []);
  }, [selectedItems.locations, locations, disableMultipleItems, setSelectedItems]);

  const handleDeleteItems = React.useCallback(() => {
    deleteItems(selectedItems[list]);
    setSelectedItems(list, []);
  }, [list, selectedItems, deleteItems, setSelectedItems]);

  const handleShowModal = (show: boolean, modal: keyof State) => {
    setState(prevState => ({
      ...prevState,
      [modal]: show
    }));
  };

  const handleShowConcentricLocationsModal = (show: boolean) => {
    setState(prevState => ({
      ...prevState,
      showConcentricLocationsModal: show
    }));
  };

  const handleSetConcentricLocations = () => {
    handleShowConcentricLocationsModal(false);
    setSelectedItems(list, []);
  };

  const attributionZones: AttributionZones = useMemo(() => {
    return Object.entries(allAttributionZones).reduce((acc, [venueId, attrData]) => {
      return {
        ...acc,
        ...(!attrData.deleted && { [venueId]: attrData })
      };
    }, {});
  }, [allAttributionZones]);

  const handleVenuesAttributionSettingsModalToggle = () => {
    setState(prevState => ({
      ...prevState,
      showVenuesAttributionSettingsModal: !prevState.showVenuesAttributionSettingsModal
    }));
  };

  const handleEnableAttribution = React.useCallback(() => {
    const attributionMode = resolveAttributionMode(activeSubsection, temporaryVenues);
    const needsSettingsUpdate = needsAttributionSettingsUpdate(attributionSettings, attributionMode);
    if (needsSettingsUpdate) {
      setAttributionSettings(
        attributionMode === 'footfall'
          ? buildEnabledFootfallAttributionSettings(attributionSettings)
          : {
              ...attributionSettings,
              driveToStore: {
                enabled: true
              },
              enabled: true,
              settings: {
                ...attributionSettings?.settings,
                visits: true
              }
            }
      );
    }
    const venuesToEnableAttribution = temporaryVenues.filter(({ _id }) => selectedItems.venues.includes(_id!));
    createAttributionZonesForVenues(venuesToEnableAttribution, attributionMode);
    setSelectedItems(list, []);
  }, [
    activeSubsection,
    attributionSettings,
    allAttributionZones,
    createAttributionZonesForVenues,
    selectedItems.venues,
    setAttributionSettings,
    temporaryVenues
  ]);

  const handleDisableAttribution = React.useCallback(() => {
    const { noAttributionIsCurrentlyEnabled, updatedAttributionLocations } = disableAttributionLocations(
      allAttributionZones,
      selectedItems.venues
    );
    setAttributionZones(updatedAttributionLocations);
    if (noAttributionIsCurrentlyEnabled) {
      setAttributionSettings({
        ...attributionSettings,
        driveToStore: {
          enabled: false
        },
        enabled: false,
        settings: {
          traffic: false,
          visits: false
        }
      });
    }
    setSelectedItems(list, []);
  }, [attributionSettings, selectedItems.venues, allAttributionZones, setAttributionZones]);

  const handleAddLocation = React.useCallback(
    async (radius?: number) => {
      const selectedVenuesIds = selectedItems.venues.filter(venueId =>
        selectedVenuesWithNoActivationAreas.includes(venueId)
      );
      const venuesToProcess = selectedVenuesIds.reduce(
        (acc: { existing: string[]; new: string[] }, venueId: string) => {
          const existingLocationsWithAssociatedVenues = fullShapes.some(
            location => location.shop === venueId && !location.deleted
          );

          if (existingLocationsWithAssociatedVenues) {
            acc.existing.push(venueId);
          } else {
            acc.new.push(venueId);
          }
          return acc;
        },
        {
          existing: [],
          new: []
        }
      );

      if (venuesToProcess.existing.length > 0) {
        showNotification(
          'map',
          formatMessage({
            defaultMessage:
              "There are attribution POIs that already have an associated activation area. For these attribution POIs, the activation area won't be created.",
            id: 'multiFlightListsMap.actionButtons.notification.venuesAssociated'
          })
        );
      }

      if (venuesToProcess.new.length > 0) {
        try {
          setState(prevState => ({
            ...prevState,
            addLocationStatus: 'loading'
          }));
          await onAddLocationsForVenueIds(venuesToProcess.new, radius);
          showNotification(
            'map',
            formatMessage(
              {
                defaultMessage: `${venuesToProcess.new.length} activation areas have been created`,
                id: 'multiFlightListsMap.actionButtons.notification.totalLocationsCreated'
              },
              {
                count: venuesToProcess.new.length,
                value: venuesToProcess.new.length
              }
            )
          );
          setState(prevState => ({
            ...prevState,
            addLocationStatus: 'success',
            showLocationRadiusModal: false
          }));
        } catch {
          showNotification(
            'map',
            formatMessage({
              defaultMessage: 'An error occurred while trying to create the activation areas',
              id: 'multiFlightListsMap.actionButtons.notification.locationError'
            })
          );
          setState(prevState => ({
            ...prevState,
            addLocationStatus: 'error',
            showLocationRadiusModal: false
          }));
        } finally {
          setSelectedItems(list, []);
        }
      }
    },
    [selectedItems.venues, fullShapes, onAddLocationsForVenueIds, formatMessage, setSelectedItems]
  );

  const handleClickLocationRadius = React.useCallback(() => {
    const selectedVenuesIds = selectedItems.venues;
    const venuesToProcess = selectedVenuesIds.reduce(
      (acc: { existing: string[]; new: string[] }, venueId: string) => {
        const existingLocationsWithAssociatedVenues = fullShapes.some(
          location => location.shop === venueId && !location.deleted
        );

        if (existingLocationsWithAssociatedVenues) {
          acc.existing.push(venueId);
        } else {
          acc.new.push(venueId);
        }
        return acc;
      },
      {
        existing: [],
        new: []
      }
    );

    if (venuesToProcess.existing.length === selectedVenuesIds.length) {
      showNotification(
        'map',
        formatMessage({
          defaultMessage: 'All selected venues already have associated locations.',
          id: 'multiFlightLocations.listActionButtons.allVenuesHaveAssociatedLocations'
        })
      );
      return;
    }

    handleShowModal(true, 'showLocationRadiusModal');
  }, [selectedItems.venues, fullShapes, formatMessage]);

  const handleSetSmartLocation = (isSmart: boolean) => {
    setLocationSmart(isSmart);
    setSelectedItems(list, []);
  };

  const handleEditRadius = async (radiusInMeters: number) => {
    const selectedLocationIds = selectedItems.locations ?? [];
    const updatedZones = locations
      .filter(l => !l.deleted && selectedLocationIds.includes(l._id!))
      .map(l => ({ _id: l._id, radius: radiusInMeters }));
    await updateZone(updatedZones);
    handleShowModal(false, 'showEditRadiusModal');
    setSelectedItems(list, []);
  };

  const hasSetSmartLocationOption: boolean =
    checkUserResourcePermissions('smartGroups', user, userRoles) &&
    selectedItems.locations?.length > 0 &&
    locations.filter(l => !l.deleted && selectedItems.locations!.includes(l._id!)).some(l => !l.smart);

  const hasUnsetSmartLocationOption: boolean =
    checkUserResourcePermissions('smartGroups', user, userRoles) &&
    selectedItems.locations?.length > 0 &&
    locations.filter(l => !l.deleted && selectedItems.locations!.includes(l._id!)).some(l => l.smart);

  const hasHiddenLocations: boolean =
    selectedItems.locations?.length > 0 &&
    locations.filter(l => !l.deleted && selectedItems.locations!.includes(l._id!)).some(l => !l.visible);

  const hasVisibleLocations: boolean =
    selectedItems.locations?.length > 0 &&
    locations.filter(l => !l.deleted && selectedItems.locations!.includes(l._id!)).some(l => l.visible);

  const groupActions = getGroupActionsAvailability({
    addableGroupCount: addableGroups.length,
    editableGroupCount: editableGroups.length,
    selectedCount: selectedItems[list]?.length ?? 0
  });

  const groupButtons: ListActionButton[] = [
    {
      disabled: !groupActions.newGroup,
      icon: 'fa-regular fa-folder-plus',
      id: 'NewGroupOption',
      onClick: () => openGroupModal({ mode: 'new' }),
      text: formatMessage({
        defaultMessage: 'New group',
        id: 'multiFlightListsMap.actionButtons.newGroup'
      })
    },
    {
      disabled: !groupActions.addToGroup,
      icon: 'fa-regular fa-folder-open',
      id: 'AddToGroupOption',
      onClick: () => openGroupModal({ mode: 'add' }),
      text: formatMessage({
        defaultMessage: 'Add to existing group',
        id: 'multiFlightListsMap.actionButtons.addToExistingGroup'
      })
    },
    {
      disabled: !groupActions.editGroups,
      icon: 'fa-solid fa-pen-to-square',
      id: 'EditGroupOption',
      onClick: () => openGroupModal({ mode: 'edit' }),
      requiresSelection: false,
      text: formatMessage({
        defaultMessage: 'Edit groups',
        id: 'multiFlightListsMap.actionButtons.editGroups'
      })
    }
  ];

  let selectedShopsIdsWithAttribution: string[] = [];
  let selectedVenuesHaveAttribution = false;
  let selectedVenuesHaveNoAttribution = false;
  if (list === 'venues') {
    selectedShopsIdsWithAttribution =
      Object.values(allAttributionZones)
        .filter(aZ => {
          return aZ.torusConfig?.some(tC => !tC.deleted) && selectedItems.venues.includes(aZ.shop!);
        })
        .map(({ shop }) => shop!) || [];
    selectedVenuesHaveAttribution = selectedItems.venues.some(shopId =>
      selectedShopsIdsWithAttribution.includes(shopId)
    );
    selectedVenuesHaveNoAttribution = selectedItems.venues.some(
      shopId => !selectedShopsIdsWithAttribution.includes(shopId)
    );
  }

  const selectedVenuesWithNoActivationAreas = selectedItems.venues.filter(venueId => {
    return !locations.some(loc => !loc.deleted && loc.shop && loc.shop === venueId);
  });

  const actionButtonsByType: Record<ListType, ListActionButton[]> = {
    locations: [
      {
        icon: 'fa-regular fa-pen-to-square',
        id: 'EditRadiusOption',
        onClick: () => handleShowModal(true, 'showEditRadiusModal'),
        text: formatMessage({
          defaultMessage: 'Edit radius',
          id: 'multiFlightListsMap.actionButtons.editRadius'
        })
      },
      ...(hasHiddenLocations
        ? [
            {
              icon: 'fa-regular fa-power-off',
              id: 'TurnAllOnOption',
              onClick: handleEnableLocationsClick,
              text: formatMessage({
                defaultMessage: 'Turn all on',
                id: 'multiFlightListsMap.actionButtons.turnAllOn'
              })
            }
          ]
        : []),
      ...(hasVisibleLocations
        ? [
            {
              icon: 'fa-kit fa-toggle-off',
              id: 'TurnAllOffOption',
              onClick: handleDisableLocationsClick,
              text: formatMessage({
                defaultMessage: 'Turn all off',
                id: 'multiFlightListsMap.actionButtons.turnAllOff'
              })
            }
          ]
        : []),
      ...(hasSetSmartLocationOption
        ? [
            {
              icon: 'fa-regular fa-circle-check',
              id: 'SetSmartSettingsOption',
              onClick: () => handleSetSmartLocation(true),
              text: formatMessage({
                defaultMessage: 'Set as Smart Location',
                id: 'multiFlightListsMap.actionButtons.setSmartLocation'
              })
            }
          ]
        : []),
      ...(hasUnsetSmartLocationOption
        ? [
            {
              icon: 'fa-regular fa-circle-xmark',
              id: 'UnsetSmartSettingsOption',
              onClick: () => handleSetSmartLocation(false),
              text: formatMessage({
                defaultMessage: 'Unset as Smart Location',
                id: 'multiFlightListsMap.actionButtons.unsetSmartLocation'
              })
            }
          ]
        : []),
      ...groupButtons,
      {
        icon: 'fa-regular fa-trash',
        id: 'DeleteItemsOption',
        onClick: handleDeleteItems,
        text: formatMessage({
          defaultMessage: 'Delete items',
          id: 'multiFlightListsMap.actionButtons.deleteItemsSelected'
        })
      }
    ],
    screens: [
      ...groupButtons,
      {
        icon: 'fa-regular fa-trash',
        id: 'DeleteItemsOption',
        onClick: handleDeleteItems,
        text: formatMessage({
          defaultMessage: 'Delete items',
          id: 'multiFlightListsMap.actionButtons.deleteItemsSelected'
        })
      }
    ],
    venues: [
      ...(type !== 'dooh' && selectedVenuesWithNoActivationAreas.length > 0
        ? [
            {
              icon: 'fa-regular fa-circle-dashed',
              id: 'AddLocationOption',
              loading: addLocationStatus === 'loading',
              onClick: handleClickLocationRadius,
              text: formatMessage({
                defaultMessage: 'Set as activation area',
                id: 'multiFlightListsMap.actionButtons.setAsActivationArea'
              })
            }
          ]
        : []),
      ...groupButtons,
      ...(selectedVenuesHaveNoAttribution
        ? [
            {
              icon: 'fa-regular fa-power-off',
              id: 'EnableAttributionOption',
              onClick: handleEnableAttribution,
              text: formatMessage({
                defaultMessage: 'Enable attribution',
                id: 'multiFlightListsMap.actionButtons.enableAttribution'
              })
            }
          ]
        : []),
      ...(selectedVenuesHaveAttribution
        ? [
            {
              icon: 'fa-kit fa-toggle-off',
              id: 'DisableAttributionOption',
              onClick: handleDisableAttribution,
              text: formatMessage({
                defaultMessage: 'Disable attribution',
                id: 'multiFlightListsMap.actionButtons.disableAttribution'
              })
            }
          ]
        : []),
      ...(attributionSettings?.enabled && !attributionSettings.driveToStore?.enabled
        ? [
            {
              icon: 'fa-regular fa-bullseye',
              id: 'AddAttributionOption',
              onClick: handleVenuesAttributionSettingsModalToggle,
              text: formatMessage({
                defaultMessage: 'Add Attribution',
                id: 'multiFlightListsMap.actionButtons.addAttribution'
              })
            }
          ]
        : [])
    ]
  };

  const addConcentricLocationsOption: boolean =
    selectedItems.locations?.length > 0 &&
    locations.filter(l => !l.deleted && selectedItems.locations!.includes(l._id!)).every(l => l.type === 'circle');

  if (addConcentricLocationsOption) {
    actionButtonsByType.locations.push({
      icon: 'fa-light fa-circle-dot',
      id: 'AddConcentricLocationsOption',
      onClick: () => handleShowConcentricLocationsModal(true),
      text: formatMessage({
        defaultMessage: 'Concentric Activation Areas',
        id: 'multiFlightListsMap.actionButtons.concentricLocations'
      })
    });
  }

  const hasSelectedItems = !!selectedItems[list]?.[0];
  // Without a selection only the entries that work on no rows (e.g. "Edit groups") are offered
  const buttons = actionButtonsByType[list].filter(button => hasSelectedItems || button.requiresSelection === false);
  const hasEnabledButtons = buttons.some(button => !button.disabled);
  const hide = actionButtonsByType[list].length === 0;

  if (hide) {
    return null;
  }

  const handleVenuesAttributionSettingsSave = async (
    radiusList: VenuesAttributionSettingsModalProps['radiusConfiguration']
  ) => {
    const attributionZonesShops = Object.values(attributionZones).map(attrZone => attrZone.shop);

    let torusZonesEdited = {};
    let torusZonesNew = {};
    const torusZonesExists = selectedItems.venues.filter(selectedElementId =>
      attributionZonesShops.includes(selectedElementId)
    );
    const torusZonesDoesntExist = selectedItems.venues.filter(
      selectedElementId => !attributionZonesShops.includes(selectedElementId)
    );

    if (torusZonesExists.length) {
      torusZonesEdited = Object.entries(attributionZones).reduce((acc, [key, attrZone]) => {
        const attrConfig: Record<string, any> | undefined = radiusList?.filter((r: any) => r.id === attrZone.shop)[0];
        if (torusZonesExists.includes(attrZone.shop as string)) {
          return {
            ...acc,
            [key]: {
              ...attrZone,
              attributionMode: 'footfall',
              isModified: true,
              torusConfig: attrConfig?.radius.map((r: number) => ({
                deleted: false,
                group: null,
                name: `${attrZone.name} - ${r}`,
                radius: r,
                visible: true
              })),
              visible: attrConfig?.visible
            }
          };
        }
        return acc;
      }, {});
    }

    if (torusZonesDoesntExist.length) {
      const newItem = temporaryVenues.filter(
        venue => venue._id && selectedItems.venues.includes(venue._id) && torusZonesDoesntExist.includes(venue._id)
      );
      // getAttributionZonesForPOI (sonata-platform-components) doesn't know about
      // attributionMode — it's a platform-v2-only field used to tell footfall/proximity
      // zones apart on the map. This button only ever appears for footfall-only entities
      // (see the AddAttributionOption visibility check above), so tag every new zone here.
      const rawTorusZonesNew = getAttributionZonesForPOI(newItem, radiusList, entityId, entityType);
      torusZonesNew = Object.fromEntries(
        Object.entries(rawTorusZonesNew).map(([zoneId, zone]) => [
          zoneId,
          { ...(zone as Record<string, unknown>), attributionMode: 'footfall' }
        ])
      );
    }

    const torusZones = {
      ...torusZonesEdited,
      ...torusZonesNew
    };

    const attributionData = await getNewAttributionZonesData(attributionZones, torusZones);

    // mergeAttributionZones (inside getNewAttributionZonesData) builds the result from
    // previousZone, so isModified:true set on torusZonesEdited entries is silently dropped.
    // Re-apply the flag here for all zones that were part of the edit batch.
    const editedZoneIds = new Set(Object.keys(torusZonesEdited as Record<string, unknown>));
    const attributionDataWithFlags = editedZoneIds.size
      ? (Object.fromEntries(
          Object.entries(attributionData).map(([id, zone]) => [
            id,
            editedZoneIds.has(id) ? { ...zone, isModified: true } : zone
          ])
        ) as AttributionZones)
      : attributionData;

    setAttributionZones(attributionDataWithFlags);
  };

  return (
    <div className="actionButtons" id={id}>
      <ContextualMenu id={'list-action-contextualMenu'} placement="bottom-start">
        <ContextualMenu.Trigger>
          <ButtonUI
            disabled={!hasEnabledButtons}
            icon="fa-regular fa-ellipsis-vertical"
            id={'list-action-buttons-trigger'}
            size="ultraSmall"
            type="secondary"
          />
        </ContextualMenu.Trigger>
        {buttons.map(button => (
          <ContextualMenu.Item
            disabled={button.disabled}
            icon={button.icon}
            id={`list-action-button-${button.id}`}
            key={button.id}
            onClick={button.disabled ? undefined : button.onClick}
            text={button.text}
          />
        ))}
      </ContextualMenu>
      {showConcentricLocationsModal && list === 'locations' && (
        <ConcentricLocationsModal
          isModalOpened={showConcentricLocationsModal}
          itemIds={selectedItems[list]}
          onModalClose={() => handleShowConcentricLocationsModal(false)}
          onModalSave={handleSetConcentricLocations}
        />
      )}
      {showVenuesAttributionSettingsModal && list === 'venues' && (
        <VenuesAttributionSettingsModal
          isOpen={showVenuesAttributionSettingsModal}
          onChange={handleVenuesAttributionSettingsSave}
          onClose={handleVenuesAttributionSettingsModalToggle}
          radiusConfiguration={getAttrConfig(attributionZones, selectedItems.venues)}
          user={user}
          userRoles={userRoles}
          venuesAttributionSettings={attributionSettings}
        />
      )}
      {showLocationRadiusModal && list === 'venues' && (
        <AttributionRadiusPrompt
          onCancel={() => handleShowModal(false, 'showLocationRadiusModal')}
          onConfirm={meters => handleAddLocation(meters / 1000)}
        />
      )}
      {showEditRadiusModal && list === 'locations' && (
        <AttributionRadiusPrompt
          onCancel={() => handleShowModal(false, 'showEditRadiusModal')}
          onConfirm={handleEditRadius}
        />
      )}
    </div>
  );
}
export default ListActionButtons;
